import { useEffect, useState } from "react";
import {
  DEFAULT_HEALTH_FACILITY_FEATURE_CONFIG,
  HealthCareFacilityModel,
} from "../data-model/healthFacility";

interface UseFacilityFormDataReturn {
  facilityData: HealthCareFacilityModel;
  setFacilityData: React.Dispatch<
    React.SetStateAction<HealthCareFacilityModel>
  >;
}

/**
 * useFacilityFormData
 *
 * Custom hook that manages facility form data state and initializes it from props.
 * This hook:
 * - Manages the facility form data state
 * - Automatically initializes form data when props.data changes (for edit mode)
 * - Merges `feature_config` with defaults for any missing keys when loading props
 *
 * @param {HealthCareFacilityModel | null} propsData - The facility data from props (null for add mode)
 * @param {HealthCareFacilityModel} initialData - The initial form data structure
 * @returns {UseFacilityFormDataReturn} Return object containing:
 * @returns {HealthCareFacilityModel} return.facilityData - Current facility form data
 * @returns {React.Dispatch<React.SetStateAction<HealthCareFacilityModel>>} return.setFacilityData - Function to update facility form data
 */
export const useFacilityFormData = (
  propsData: HealthCareFacilityModel | null,
  initialData: HealthCareFacilityModel,
): UseFacilityFormDataReturn => {
  const [facilityData, setFacilityData] =
    useState<HealthCareFacilityModel>(initialData);

  useEffect(() => {
    if (propsData) {
      const newData: HealthCareFacilityModel = {
        client_id: propsData?.client_id || "",
        Name: propsData?.Name ?? "",
        NPI: propsData?.NPI ?? "",
        TaxID: propsData?.TaxID ?? "",
        PhoneNumber: propsData?.PhoneNumber ?? "",
        FhirId: propsData?.FhirId ?? "",
        is_external_organization: propsData?.is_external_organization ?? false,
        Address: {
          StreetAddress: propsData.Address?.StreetAddress || "",
          City: propsData?.Address?.City ?? "",
          State: propsData?.Address?.State ?? "",
          ZIP: propsData?.Address?.ZIP ?? "",
        },
        emr_name: propsData?.emr_name ?? "",
        fhir_client: propsData?.fhir_client ?? "",
        RisaFaxNumber: propsData?.RisaFaxNumber ?? "",
        RisaPhoneNumber: propsData?.RisaPhoneNumber ?? "",
        updated_at: propsData?.updated_at || "",
        updated_by: propsData?.updated_by || "",
        onco_emr_base_url: propsData?.onco_emr_base_url ?? "",
        inactive_insurance_bo_status:
          propsData?.inactive_insurance_bo_status ?? "",
        auth_letter_type: propsData?.auth_letter_type ?? "",
        feature_config: {
          ...DEFAULT_HEALTH_FACILITY_FEATURE_CONFIG,
          ...propsData.feature_config,
        },
        analytics_url: propsData?.analytics_url ?? "",
        auth_status_filter: propsData?.auth_status_filter ?? [],
        corresponding_internal_org_id:
          propsData?.corresponding_internal_org_id ?? "",
        category: propsData?.category ?? "",
      };
      setFacilityData(newData);
    }
  }, [propsData]);

  return {
    facilityData,
    setFacilityData,
  };
};
